import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext'; 
import { FiSun, FiMoon, FiBell, FiSearch } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const API_URL = 'https://app.thundermaxhome.com/api.php';

export default function AdminTopBar() {
  const { user } = useAuth();
  const navigate = useNavigate();
 
  // 🔍 State & Logic for Global Menu Search with Autocomplete
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef(null);
 
  const menuConfig = {
    dashboard: { to: '/admin/dashboard', label: 'Dashboard' },
    customers: { label: 'การจัดการลูกค้า', sub: [
      { to: '/admin/data/customers', label: 'ข้อมูลลูกค้า (CRM)' },
      { to: '/admin/jobs/approve', label: 'จัดการคิว (อนุมัติจอง)' },
      { to: '/admin/blocked-dates', label: 'จัดการวันคิวเต็ม (Slots)' },
      { to: '/admin/jobs/all', label: 'ข้อมูลงานทั้งหมด' },
      { to: '/admin/jobs/create', label: 'คีย์แจ้งงานใหม่' },
      { to: '/admin/jobs/inspect', label: 'ประเมินหน้างานช่าง' }
    ]},
    homepage: { label: 'จัดการหน้าบ้านหลัก', sub: [
      { to: '/admin/web-manager', label: 'ปรับแต่งเนื้อหาเว็บ (CMS)' },
      { to: '/admin/google-drive', label: 'Google Drive คลังรูป' }
    ]},
    stock: { label: 'คลังสินค้า & สต๊อก', sub: [
      { to: '/admin/products', label: 'สต๊อกสินค้าแอร์บ้าน' },
      { to: '/admin/sales-data', label: 'ข้อมูลการขาย/สต๊อกเซลส์' }
    ]},
    hr: { label: 'ฝ่ายบุคคล (HR)', sub: [
      { to: '/admin/data/employees', label: 'ข้อมูลพนักงาน' },
      { to: '/admin/commissions', label: 'ค่าคอมมิชชันแนะนำลูกค้า' },
      { to: '/admin/hr', label: 'ฝ่ายบุคคล & ขาดลาสาย' },
      { to: '/admin/kpi', label: 'ประเมินผลงาน & KPI' },
      { to: '/admin/roles', label: 'จัดการสิทธิ์เข้าใช้งาน' }
    ]},
    chat: { label: 'Chat & Marketing', sub: [
      { to: '/admin/chat', label: 'รวมแชทลูกค้า (Chat)' },
      { to: '/admin/facebook-automation', label: 'โพสต์แฟนเพจอัตโนมัติ' }
    ]},
    finance: { label: 'ประวัติ & การเงิน', sub: [
      { to: '/admin/finance', label: 'ประวัติ & การเงิน' },
      { to: '/admin/finance/invoice', label: 'ออกใบเสร็จ/ใบกำกับภาษี' }
    ]},
    helpdesk: { label: 'IT Support', sub: [
      { to: '/admin/helpdesk/create', label: 'แจ้งปัญหา / ขอเพิ่มระบบ' },
      { to: '/admin/helpdesk/tickets', label: 'ประวัติตั๋ว (Tickets)' }
    ]},
    system: { label: 'ฝ่ายบริหาร', sub: [
      { to: '/admin/exec-dashboard', label: 'แดชบอร์ดฝ่ายบริหาร' },
      { to: '/admin/export-data', label: 'ส่งออกข้อมูลบริษัท' },
      { to: '/admin/seo', label: 'ตั้งค่าระบบ SEO หน้าเว็บ' },
      { to: '/admin/settings', label: 'ตั้งค่าระบบ API' }, 
      { to: '/admin/logs', label: 'ระบบ Log การใช้งาน' },
      { to: '/admin/ai-workflow', label: 'AI Agent Workflows' },
      { to: '/admin/profile', label: 'ตั้งค่าโปรไฟล์' }
    ]}
  };
 
  const hasSubMenuAccess = (to) => {
    if (!user) return false;
    if (user.role === 'owner' || user.role === 'it') return true;
    
    let userPerms = [];
    if (user.permissions) {
      try {
        const parsed = typeof user.permissions === 'string' ? JSON.parse(user.permissions) : user.permissions;
        userPerms = Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        userPerms = [];
      }
    }
 
    switch (to) {
      case '/admin/data/customers':
        return userPerms.includes('customers');
      case '/admin/data/employees':
        return userPerms.includes('employees');
      case '/admin/roles':
        return userPerms.includes('roles');
      case '/admin/products':
      case '/admin/profile':
        return true; 
      case '/admin/commissions':
      case '/admin/logs':
      case '/admin/settings':
      case '/admin/web-manager':
      case '/admin/sales-data':
      case '/admin/ai-workflow':
      case '/admin/kpi':
      case '/admin/hr':
      case '/admin/exec-dashboard':
      case '/admin/google-drive':
        return user.role === 'owner' || user.role === 'it' || user.role === 'admin';
      default:
        return true;
    }
  };
 
  // Close search dropdown on click outside
  useEffect(() => {
    function handleClickOutsideSearch(event) {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutsideSearch);
    return () => document.removeEventListener("mousedown", handleClickOutsideSearch);
  }, []);
 
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }
 
    const filtered = [];
    // 1. Check Dashboard
    if ('dashboard'.includes(query.toLowerCase()) || 'แดชบอร์ด'.includes(query)) {
      filtered.push({ to: '/admin/dashboard', label: 'Dashboard', categoryLabel: 'แดชบอร์ด' });
    }
 
    // 2. Check sub-menus
    Object.keys(menuConfig).forEach(key => {
      if (key === 'dashboard') return;
      const cat = menuConfig[key];
      const subMenus = cat.sub || [];
      subMenus.forEach(sub => {
        if (hasSubMenuAccess(sub.to)) {
          if (sub.label.toLowerCase().includes(query.toLowerCase()) || cat.label.toLowerCase().includes(query.toLowerCase())) {
            filtered.push({ to: sub.to, label: sub.label, categoryLabel: cat.label });
          }
        }
      });
    });
 
    setSuggestions(filtered.slice(0, 10)); // Limit to 10 suggestions
    setShowSuggestions(true);
  };
 
  const handleSuggestionClick = (to) => {
    navigate(to);
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
  };
 
  const clearSearch = () => {
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
  };
  const [activeCategory, setActiveCategory] = useState('dashboard');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [pendingJobsCount, setPendingJobsCount] = useState(0); 
  const [inspectJobsCount, setInspectJobsCount] = useState(0);
  const [unreadChatsCount, setUnreadChatsCount] = useState(0);
  const [pendingTicketsCount, setPendingTicketsCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [hasOpenedDropdown, setHasOpenedDropdown] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(localStorage.getItem('theme') === 'dark' || !localStorage.getItem('theme'));
  
  // ใช้ useRef เพื่อเก็บบันทึกรหัสงานเก่าที่ประมวลผลแล้ว ป้องกันการเด้งเตือนซ้ำซ้อนตอนเริ่มต้นโหลดหน้าเว็บ
  const prevJobIdsRef = useRef([]);
  const prevInspectJobIdsRef = useRef([]);
  const isInitialMount = useRef(true);
  const dropdownRef = useRef(null);

  // ปิด Dropdown เมื่อคลิกนอกขอบเขต
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 🕒 อัปเดตเวลา
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // 🌗 จัดการธีม
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  // ตรวจสอบสิทธิ์การเตือนและสิทธิ์เข้าถึงหน้ารับอนุมัติงานจอง
  const hasApprovePermission = () => {
    if (!user) return false;
    if (user.role === 'owner' || user.role === 'it' || user.role === 'admin') return true;
    
    let perms = [];
    if (user.permissions) {
      try {
        perms = typeof user.permissions === 'string' ? JSON.parse(user.permissions) : user.permissions;
      } catch (e) {
        perms = [];
      }
    }
    return Array.isArray(perms) && (perms.includes('manage_jobs') || perms.includes('approve_bookings'));
  };

  // 🚀 ดึงจำนวนงานใหม่, แชทใหม่, และตั๋วไอที ทุกๆ 8 วินาที
  useEffect(() => {
    const fetchNotificationCounts = async () => {
      // 1. ดึงคิวงานที่ยังไม่ได้อนุมัติ และ งานที่รอตรวจสอบประเมิน
      if (hasApprovePermission()) {
        try {
          const res = await fetch(`${API_URL}?action=get_all_jobs`);
          const data = await res.json();
          if (data.success) {
            const newJobs = data.data.filter(job => job.status === 'pending_approval');
            const inspectJobs = data.data.filter(job => job.isReportPending == 1);
            
            const currentJobIds = newJobs.map(job => job.id);
            const currentInspectJobIds = inspectJobs.map(job => job.id);

            if (!isInitialMount.current) {
              const freshIds = currentJobIds.filter(id => !prevJobIdsRef.current.includes(id));
              const freshInspectIds = currentInspectJobIds.filter(id => !prevInspectJobIdsRef.current.includes(id));
              
              if (freshIds.length > 0 || freshInspectIds.length > 0) {
                // หากพบงานใหม่หรือรายงานใหม่ ให้เคลียร์สถานะ hasOpenedDropdown เพื่อแจ้งเตือนเสียงระฆังดังใหม่
                setHasOpenedDropdown(false);
                
                // แจ้งเตือนคิวงานใหม่
                freshIds.forEach(id => {
                  const jobDetails = newJobs.find(j => j.id === id);
                  const customerName = jobDetails ? jobDetails.customerName : 'ลูกค้า';
                  const jobTypeDisplay = jobDetails?.job_type === 'aircon' ? 'ล้าง/ติดตั้งแอร์' : 'งานซ่อมบำรุง';

                  Swal.fire({
                    title: '🔔 คำขอจองคิวใหม่!',
                    html: `มีคิวจองออนไลน์เข้ามาใหม่<br/><b>ลูกค้า:</b> ${customerName}<br/><b>บริการ:</b> ${jobTypeDisplay}`,
                    icon: 'info',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: true,
                    confirmButtonText: 'ดูรายการอนุมัติ',
                    confirmButtonColor: '#2563eb',
                    timer: 10000,
                    timerProgressBar: true,
                    showCloseButton: true
                  }).then((result) => {
                    if (result.isConfirmed) {
                      navigate('/admin/jobs/approve');
                    }
                  });
                });

                // แจ้งเตือนรายงานประเมินหน้างานใหม่
                freshInspectIds.forEach(id => {
                  const jobDetails = inspectJobs.find(j => j.id === id);
                  const customerName = jobDetails ? jobDetails.customerName : 'ลูกค้า';
                  const techName = jobDetails ? jobDetails.technicianName : 'ช่าง';

                  Swal.fire({
                    title: '🛠️ ช่างส่งรายงานหน้างาน!',
                    html: `<b>ช่าง:</b> ${techName}<br/><b>ลูกค้า:</b> ${customerName}<br/><span style="color:red; font-size:12px;">รอแอดมินประเมินราคา</span>`,
                    icon: 'warning',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: true,
                    confirmButtonText: 'ตรวจสอบรายการ',
                    confirmButtonColor: '#dc2626',
                    timer: 10000,
                    timerProgressBar: true,
                    showCloseButton: true
                  }).then((result) => {
                    if (result.isConfirmed) {
                      navigate('/admin/jobs/inspect');
                    }
                  });
                });
              }
            } else {
              isInitialMount.current = false;
            }

            prevJobIdsRef.current = currentJobIds;
            prevInspectJobIdsRef.current = currentInspectJobIds;
            setPendingJobsCount(newJobs.length);
            setInspectJobsCount(inspectJobs.length);
          }
        } catch (err) { console.error(err); }
      }

      // 2. ดึงจำนวนข้อความแชทใหม่ที่ยังไม่ได้อ่าน
      try {
        const res = await fetch(`${API_URL}?action=get_chat_rooms`);
        const data = await res.json();
        if (data.success) {
          const sumUnread = data.data.reduce((sum, c) => sum + Number(c.unread), 0);
          setUnreadChatsCount(sumUnread);
        }
      } catch (err) { console.error(err); }

      // 3. ดึงจำนวนตั๋วไอทีรอแก้ไข (IT Helpdesk status = pending)
      try {
        const res = await fetch(`${API_URL}?action=get_tickets`);
        const data = await res.json();
        if (data.success && data.data) {
          const pendingTickets = data.data.filter(t => t.status === 'pending');
          setPendingTicketsCount(pendingTickets.length);
        }
      } catch (err) { console.error(err); }
    };

    fetchNotificationCounts();
    const interval = setInterval(fetchNotificationCounts, 8000);
    return () => clearInterval(interval);
  }, [user]);

  const totalNotifications = pendingJobsCount + inspectJobsCount + unreadChatsCount + pendingTicketsCount;

  const handleBellClick = () => {
    setShowDropdown(!showDropdown);
    setHasOpenedDropdown(true);
  };

  return (
    // 💡 ลดความสูงจาก h-16 เป็น h-14, ลด padding
    <div className="h-14 flex justify-between items-center bg-white dark:bg-[#1e293b] px-4 shadow-sm border-b border-gray-200 dark:border-[#334155] transition-colors duration-300">
      
      {/* 🔍 Global Menu Search Box with Autocomplete Dropdown */}
      <div className="relative w-56 sm:w-64 md:w-80 select-none z-[999]" ref={searchRef}>
        <div className="relative">
          <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 dark:text-gray-500">
            <FiSearch size={14} />
          </span>
          <input
            type="text"
            placeholder="ค้นหาเมนูการทำงานหลังบ้าน..."
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => setShowSuggestions(true)}
            className="w-full pl-9 pr-8 py-1.5 bg-gray-50 dark:bg-[#0f172a] border border-gray-250 dark:border-[#334155] rounded-xl text-xs text-gray-800 dark:text-white outline-none focus:border-indigo-500 transition-all font-bold placeholder:text-gray-400 dark:placeholder:text-gray-500 placeholder:font-normal"
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-650 dark:hover:text-white bg-transparent border-none cursor-pointer p-0"
            >
              ✕
            </button>
          )}
        </div>
 
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute left-0 mt-2 w-full bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-[#334155] rounded-xl shadow-2xl py-2 z-[9999] max-h-64 overflow-y-auto animate-fade-in text-xs">
            <p className="px-3.5 py-1 text-[9px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-wider border-b dark:border-[#334155] pb-1.5 mb-1.5 select-none">
              💡 เมนูแนะนำตามคำค้นหา
            </p>
            {suggestions.map((item, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleSuggestionClick(item.to)}
                className="w-full text-left px-3.5 py-2 hover:bg-indigo-50 dark:hover:bg-[#2d1b69] flex justify-between items-center text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-[#c4b5fd] transition-colors border-none bg-transparent cursor-pointer font-bold"
              >
                <span>{item.label}</span>
                <span className="text-[8px] bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider">
                  {item.categoryLabel}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
 
      <div className="flex items-center gap-4">
        {/* 🕒 วันที่ และ เวลา (เล็กลง) */}
        <div className="text-right hidden md:block">
          <p className="text-xs font-bold text-gray-800 dark:text-white">
            {currentTime.toLocaleDateString('th-TH', { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' })}
          </p>
          <p className="text-[10px] font-medium text-gray-500 dark:text-gray-400 mt-0.5">
            {currentTime.toLocaleTimeString('th-TH')} น.
          </p>
        </div>

        <div className="flex items-center gap-2 border-r pr-4 border-gray-200 dark:border-[#334155]">
          {/* 🚀 🔔 กระดิ่งแจ้งเตือน ใช้งานได้จริง! */}
          <div className="relative">
            <button 
              onClick={handleBellClick}
              className="relative p-2 rounded-full bg-gray-50 dark:bg-[#0f172a] text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              title="การแจ้งเตือนระบบ"
            >
              <FiBell size={16} />
              {totalNotifications > 0 && !hasOpenedDropdown && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 bg-red-500 text-white text-[9px] font-bold flex items-center justify-center rounded-full px-1 animate-bounce">
                  {totalNotifications}
                </span>
              )}
            </button>
            
            {showDropdown && (
              <div ref={dropdownRef} className="absolute right-0 mt-2 w-64 bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-[#334155] rounded-xl shadow-xl py-2 z-50 animate-fade-in text-xs">
                <div className="px-4 py-2 border-b border-gray-100 dark:border-[#334155] font-bold text-gray-800 dark:text-white flex justify-between items-center">
                  <span>🔔 รายการแจ้งเตือน</span>
                  {totalNotifications > 0 && (
                    <span className="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-2 py-0.5 rounded-full text-[10px]">
                      {totalNotifications}
                    </span>
                  )}
                </div>
                <div className="max-h-60 overflow-y-auto">
                  <button 
                    onClick={() => { navigate('/admin/jobs/approve'); setShowDropdown(false); }} 
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-[#131825] flex justify-between items-center border-b border-gray-100 dark:border-[#334155] text-gray-700 dark:text-gray-300 transition-colors"
                  >
                    <span>📅 คิวงานใหม่รออนุมัติ</span>
                    <span className={`px-2 py-0.5 rounded-full font-bold ${pendingJobsCount > 0 ? 'bg-amber-500 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-500'}`}>{pendingJobsCount}</span>
                  </button>

                  <button 
                    onClick={() => { navigate('/admin/jobs/inspect'); setShowDropdown(false); }} 
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-[#131825] flex justify-between items-center border-b border-gray-100 dark:border-[#334155] text-gray-700 dark:text-gray-300 transition-colors"
                  >
                    <span>🛠️ ช่างส่งรายงานประเมิน</span>
                    <span className={`px-2 py-0.5 rounded-full font-bold ${inspectJobsCount > 0 ? 'bg-red-500 text-white animate-pulse' : 'bg-gray-100 dark:bg-gray-800 text-gray-500'}`}>{inspectJobsCount}</span>
                  </button>
                  
                  <button 
                    onClick={() => { navigate('/admin/chat'); setShowDropdown(false); }} 
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-[#131825] flex justify-between items-center border-b border-gray-100 dark:border-[#334155] text-gray-700 dark:text-gray-300 transition-colors"
                  >
                    <span>💬 แชทลูกค้าใหม่ค้างอยู่</span>
                    <span className={`px-2 py-0.5 rounded-full font-bold ${unreadChatsCount > 0 ? 'bg-[#00B900] text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-500'}`}>{unreadChatsCount}</span>
                  </button>
                  
                  <button 
                    onClick={() => { navigate('/admin/helpdesk/tickets'); setShowDropdown(false); }} 
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-[#131825] flex justify-between items-center text-gray-700 dark:text-gray-300 transition-colors"
                  >
                    <span>🛟 ตั๋วไอทีรอแก้ไข</span>
                    <span className={`px-2 py-0.5 rounded-full font-bold ${pendingTicketsCount > 0 ? 'bg-indigo-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-500'}`}>{pendingTicketsCount}</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* 🌗 ปุ่มสลับ Dark/Light Mode */}
          <button 
            onClick={() => setIsDarkMode(!isDarkMode)} 
            className="p-2 rounded-full bg-gray-50 dark:bg-[#0f172a] text-gray-600 dark:text-yellow-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition"
          >
            {isDarkMode ? <FiSun size={16} /> : <FiMoon size={16} />}
          </button>
        </div>

        {/* 👤 โปรไฟล์ผู้ใช้ (ย่อส่วนลง) */}
        <div className="flex items-center gap-2.5">
          <div className="relative">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm shadow-sm">
              {user?.name ? user.name.charAt(0).toUpperCase() : 'A'}
            </div>
            <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white dark:border-[#1e293b] rounded-full"></span>
          </div>
          <div className="hidden sm:block leading-tight">
            <p className="text-xs font-bold text-gray-800 dark:text-white">{user?.name || 'แอดมิน'}</p>
            <p className="text-[9px] font-bold text-green-500">ออนไลน์</p>
          </div>
        </div>
      </div>
    </div>
  );
}